/**
 * MembershipCardDates.tsx
 *
 * The dates on a patient's membership card (the Memberships tab and the
 * patient dashboard), counting membership credits (plan 2026-10-22-001) the
 * way the membership detail does:
 *   - no unused credits: Next Billing and Paid Through, the ledger's own dates
 *   - unused credits: Next Charge, the first month no credit pays, and Paid
 *     Through counting the months the credits pay, with how many are left
 *   - cancelled with "keep" (a pending keep-through): when it cancels, which
 *     is when its credits run out
 */
import type { MembershipCardPrepay } from '@/services/memberships.service';

export function formatShortDate(value: string | null | undefined): string {
  if (!value) return '—';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(value);
  const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function DateValue({ value }: { value: string | null | undefined }) {
  return <span className="font-medium text-slate-700">{value ? formatShortDate(value) : '—'}</span>;
}

/** A pending keep-through: cancelled with "keep", it ends when its credits run out. */
export function isKeepThroughPending(prepay: MembershipCardPrepay | null | undefined): boolean {
  return prepay?.keepThroughPending === true;
}

/** An active membership's billing dates: Next Billing, or Next Charge while credits pay the months. */
export function MembershipBillingDates({ nextBillingDate, paymentThroughDate, prepay }: {
  nextBillingDate: string | null | undefined;
  paymentThroughDate: string | null | undefined;
  prepay: MembershipCardPrepay | null | undefined;
}) {
  const credits = prepay?.usableCredits ?? 0;
  if (prepay && credits > 0) {
    return (
      <div className="flex items-center gap-4 flex-wrap">
        <span>
          Next Charge: <DateValue value={prepay.nextChargeDate} />
        </span>
        <span>
          Paid Through: <DateValue value={prepay.paidThroughDate} />
        </span>
        <span className="font-medium text-emerald-700">
          {credits === 1 ? '1 credit left' : `${credits} credits left`}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4 flex-wrap">
      <span>
        Next Billing: <DateValue value={nextBillingDate} />
      </span>
      <span>
        Paid Through: <DateValue value={paymentThroughDate} />
      </span>
    </div>
  );
}

/** A cancelling membership's last day; a pending keep-through's is when its credits run out. */
export function MembershipCancelsDate({ date, prepay }: {
  date: string | null | undefined;
  prepay: MembershipCardPrepay | null | undefined;
}) {
  const keepThrough = isKeepThroughPending(prepay);
  return (
    <div>
      Cancels: <DateValue value={keepThrough ? prepay?.keepThroughEndDate ?? date : date} />
      {keepThrough ? ' (when its credits run out)' : null}
    </div>
  );
}
